import {
  cleanup,
  createAudioProcessor,
  requestMicrophoneAccess,
} from './audio';
import {
  getNoteName,
  MILESTONE_INTERVAL,
  MILESTONE_MESSAGES,
  TOTAL_KEYS,
} from './constants';
import { createDetector, type Detector, type DetectorEvent } from './detector';
import { createPianoRenderer, type PianoRenderer } from './piano';
import { generateReport, renderReport } from './report';
import {
  $,
  flashNote,
  hide,
  show,
  showMilestone,
  showStep,
  updateProgress,
  updateRecordingInfo,
} from './ui';

// State
let detector: Detector | null = null;
let piano: PianoRenderer | null = null;

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// ---- Wire up wizard ----

document.addEventListener('DOMContentLoaded', () => {
  // Step 1: Welcome → Mic Permission
  $('btn-start').addEventListener('click', () => {
    showStep('step-mic');
  });

  // Step 2: Mic Permission
  $('btn-mic').addEventListener('click', handleMicRequest);
  $('btn-mic-retry').addEventListener('click', handleMicRequest);

  // Recording controls
  $('btn-undo').addEventListener('click', handleUndo);
  $('btn-pause').addEventListener('click', handlePauseResume);

  // Report: restart
  $('btn-restart').addEventListener('click', handleRestart);
});

async function handleMicRequest(): Promise<void> {
  const errorEl = $('mic-error');
  const errorText = $('mic-error-text');
  hide(errorEl);

  try {
    await requestMicrophoneAccess();
    startRecording();
  } catch (err: any) {
    show(errorEl);
    if (err.message === 'DENIED') {
      errorText.textContent =
        'Microphone access was denied. Please allow microphone access in your browser settings and try again.';
    } else if (err.message === 'UNSUPPORTED') {
      errorText.textContent =
        "Your browser doesn't support microphone access. Please try Chrome or Safari on a modern device.";
    } else {
      errorText.textContent =
        'Something went wrong accessing the microphone. Please try again.';
    }
  }
}

function startRecording(): void {
  showStep('step-record');

  // Create piano renderer
  piano = createPianoRenderer();
  piano.render($('piano-container'));

  // Create audio processor + detector
  const processor = createAudioProcessor();
  detector = createDetector(processor, handleDetectorEvent);

  // Initial UI state
  updateProgress(1, TOTAL_KEYS);
  updateRecordingInfo(1, getNoteName(1));
  piano.updateState(1, 0);
  piano.scrollToKey(1);

  // Enable undo button only when there's something to undo
  ($('btn-undo') as HTMLButtonElement).disabled = true;

  detector.start();
}

function handleDetectorEvent(event: DetectorEvent): void {
  switch (event.type) {
    case 'key-detected': {
      const { keyNum, frequency } = event;
      const noteName = getNoteName(keyNum);
      const nextKey = keyNum + 1;

      // Visual feedback
      flashNote(noteName);
      if (piano) {
        piano.highlightKey(keyNum);
      }

      // Update UI for next key
      if (nextKey <= TOTAL_KEYS) {
        updateProgress(nextKey, TOTAL_KEYS);
        updateRecordingInfo(nextKey, getNoteName(nextKey));
        if (piano) {
          piano.updateState(nextKey, keyNum);
          piano.scrollToKey(nextKey);
        }
      } else {
        // All done - progress shows 88/88
        const fill = $('progress-fill');
        fill.style.width = '100%';
        $('progress-text').textContent = `${TOTAL_KEYS} / ${TOTAL_KEYS}`;
      }

      // Enable undo
      ($('btn-undo') as HTMLButtonElement).disabled = false;

      // Milestone check
      if (keyNum % MILESTONE_INTERVAL === 0 && keyNum < TOTAL_KEYS) {
        const msgIndex = Math.floor(keyNum / MILESTONE_INTERVAL) - 1;
        const msg = MILESTONE_MESSAGES[msgIndex % MILESTONE_MESSAGES.length];
        showMilestone(msg);
      }
      break;
    }

    case 'undo': {
      const keyNum = event.keyNum;
      updateProgress(keyNum, TOTAL_KEYS);
      updateRecordingInfo(keyNum, getNoteName(keyNum));
      if (piano) {
        piano.updateState(keyNum, keyNum - 1);
        piano.scrollToKey(keyNum);
      }
      ($('btn-undo') as HTMLButtonElement).disabled = keyNum <= 1;
      break;
    }

    case 'complete': {
      // Brief delay before showing report
      setTimeout(() => {
        if (detector) {
          detector.stop();
          const data = detector.getRecordedData();
          const report = generateReport(data);
          renderReport(report);
          showStep('step-report');
        }
      }, 800);
      break;
    }

    case 'pitch-update':
      // Could show a live frequency display here if desired
      break;
  }
}

function handleUndo(): void {
  if (detector && !detector.isComplete()) {
    detector.undo();
  }
}

function handlePauseResume(): void {
  if (!detector) return;
  const btn = $('btn-pause') as HTMLButtonElement;

  if (detector.isPaused()) {
    detector.resume();
    btn.textContent = '⏸ Pause';
  } else {
    detector.pause();
    btn.textContent = '▶ Resume';
  }
}

function handleRestart(): void {
  if (detector) {
    detector.stop();
    detector = null;
  }
  cleanup();
  piano = null;
  showStep('step-welcome');
}
